import { API_BASE_URL } from "@/constants/api";
import type { ExportFormat } from "@/lib/export-format";
import { moderationApi } from "@/services/dashboard-api.client";

export type ModerationSeverity = "low" | "medium" | "high";
export type ModerationRunAt = "before_enrichment" | "after_enrichment" | "both";
export type ModerationImageScope = "first_image" | "all_images";
export type ModerationReadiness = "preparing" | "ready" | "needs_attention";
export type ModerationReferenceRole = "flagged" | "acceptable";

export interface ModerationReferenceImage {
  assetId: string;
  assetUrl: string;
  role: ModerationReferenceRole;
  /** The reason this example was rejected, in the customer's own words. */
  caption: string | null;
}

export interface ModerationReferenceAsset {
  assetId: string;
  assetUrl: string;
  mimeType: string;
}

export interface ModerationRule {
  id: string;
  rulesetId: string;
  name: string;
  slug: string;
  description: string;
  severity: ModerationSeverity;
  runAt: ModerationRunAt;
  imageScope: ModerationImageScope;
  referenceImages: ModerationReferenceImage[];
  autoUnpublish: boolean;
  active: boolean;
  readiness: ModerationReadiness;
  statusMessage: string | null;
  version: number;
  effectiveVersion: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ModerationRuleWrite {
  name: string;
  /** Optional: derived from the name when omitted, and fixed once the rule exists. */
  slug?: string;
  description: string;
  severity: ModerationSeverity;
  runAt: ModerationRunAt;
  imageScope: ModerationImageScope;
  referenceImages: Array<{
    assetId: string;
    role: ModerationReferenceRole;
    /** Must be sent back on every write: the API replaces the list wholesale,
     *  so omitting it erases the caption. */
    caption?: string | null;
  }>;
  autoUnpublish: boolean;
}

export interface ModerationRuleVersion {
  id?: string;
  ruleId?: string;
  version: number;
  name: string;
  slug: string;
  description: string;
  severity: ModerationSeverity;
  runAt: ModerationRunAt;
  imageScope: ModerationImageScope;
  referenceImages: ModerationReferenceImage[];
  autoUnpublish: boolean;
  readiness: ModerationReadiness;
  createdAt: string;
  preparedAt?: string | null;
}

export interface ModerationRuleset {
  id: string;
  name: string;
  description: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignmentCount: number;
  rules?: ModerationRule[];
}

export type ModerationSyncStatus = "syncing" | "ready" | "needs_attention";

export interface ModerationRulesetAssignment {
  catalogueId: string;
  catalogueName?: string;
  rulesetId: string;
  rulesetName?: string;
  syncStatus?: ModerationSyncStatus;
  updatedAt?: string;
}

export interface ModerationImportPreview {
  rulesetName: string;
  ruleCount: number;
  referenceImageCount: number;
}

interface ModerationImportPreviewWire {
  schemaVersion: number;
  name: string;
  description?: string | null;
  ruleCount: number;
  referenceImageCount: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function unwrap<T>(value: unknown): T {
  if (isRecord(value) && "data" in value) return value.data as T;
  return value as T;
}

async function errorMessage(response: Response): Promise<string> {
  const payload: unknown = await response.json().catch(() => null);
  if (isRecord(payload)) {
    const nested = isRecord(payload.error) ? payload.error : null;
    const candidates = [payload.message, payload.error, nested?.message];
    const message = candidates.find(
      (candidate): candidate is string => typeof candidate === "string" && Boolean(candidate.trim())
    );
    if (message) return message;
  }
  return `Request failed (${response.status})`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  if (response.status === 204) return undefined as T;
  return unwrap<T>(await response.json());
}

async function requestOptional<T>(path: string): Promise<T | null> {
  const response = await fetch(`${API_BASE_URL}${path}`, { credentials: "include" });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await errorMessage(response));
  return unwrap<T>(await response.json());
}

function attachmentFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded.replace(/^"|"$/g, ""));
  return disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? fallback;
}

export const ModerationRulesetService = {
  listRulesets: async (): Promise<ModerationRuleset[]> => {
    return request<ModerationRuleset[]>(
      "/api/v1/moderation/rulesets?includeArchived=true"
    );
  },

  createRuleset: (input: { name: string; description?: string | null }) =>
    request<ModerationRuleset>("/api/v1/moderation/rulesets", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  getRuleset: (rulesetId: string) =>
    request<ModerationRuleset>(`/api/v1/moderation/rulesets/${rulesetId}`),

  updateRuleset: (rulesetId: string, input: { name: string; description?: string | null }) =>
    request<ModerationRuleset>(`/api/v1/moderation/rulesets/${rulesetId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  archiveRuleset: (rulesetId: string) =>
    request<ModerationRuleset>(`/api/v1/moderation/rulesets/${rulesetId}/archive`, {
      method: "POST",
    }),

  restoreRuleset: (rulesetId: string) =>
    request<ModerationRuleset>(`/api/v1/moderation/rulesets/${rulesetId}/restore`, {
      method: "POST",
    }),

  listRules: async (rulesetId: string): Promise<ModerationRule[]> => {
    return request<ModerationRule[]>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules`
    );
  },

  createRule: (rulesetId: string, input: ModerationRuleWrite) =>
    request<ModerationRule>(`/api/v1/moderation/rulesets/${rulesetId}/rules`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateRule: (
    rulesetId: string,
    ruleId: string,
    input: ModerationRuleWrite,
    expectedVersion: number
  ) =>
    request<ModerationRule>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules/${ruleId}?expectedVersion=${expectedVersion}`,
      {
      method: "PATCH",
      body: JSON.stringify(input),
      }
    ),

  setRuleActive: (rulesetId: string, ruleId: string, active: boolean) =>
    request<ModerationRule>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules/${ruleId}/active`,
      { method: "PUT", body: JSON.stringify({ active }) }
    ),

  retryRule: (rulesetId: string, ruleId: string) =>
    request<ModerationRule>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules/${ruleId}/retry`,
      { method: "POST" }
    ),

  listRuleVersions: async (rulesetId: string, ruleId: string): Promise<ModerationRuleVersion[]> => {
    return request<ModerationRuleVersion[]>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules/${ruleId}/versions`
    );
  },

  getRuleVersion: (rulesetId: string, ruleId: string, version: number) =>
    request<ModerationRuleVersion>(
      `/api/v1/moderation/rulesets/${rulesetId}/rules/${ruleId}/versions/${version}`
    ),

  listAssignments: async (rulesetId: string): Promise<ModerationRulesetAssignment[]> => {
    return request<ModerationRulesetAssignment[]>(
      `/api/v1/moderation/rulesets/${rulesetId}/assignments`
    );
  },

  getCatalogueAssignment: (catalogueId: string) =>
    requestOptional<ModerationRulesetAssignment>(
      `/api/v1/moderation/catalogues/${catalogueId}/assignment`
    ),

  setCatalogueAssignment: (catalogueId: string, rulesetId: string) =>
    request<ModerationRulesetAssignment>(
      `/api/v1/moderation/catalogues/${catalogueId}/assignment`,
      { method: "PUT", body: JSON.stringify({ rulesetId }) }
    ),

  removeCatalogueAssignment: (catalogueId: string) =>
    request<void>(`/api/v1/moderation/catalogues/${catalogueId}/assignment`, {
      method: "DELETE",
    }),

  uploadReferenceAsset: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<ModerationReferenceAsset>("/api/v1/moderation/assets", {
      method: "POST",
      body: form,
    });
  },

  exportRuleset: async (rulesetId: string, format: ExportFormat = "json") => {
    const response = await moderationApi.exportGlobalModerationRulesetRaw({ rulesetId, format });
    return {
      blob: await response.raw.blob(),
      filename: attachmentFilename(response.raw, "moderation-rules.zip"),
    };
  },

  previewImport: async (file: File): Promise<ModerationImportPreview> => {
    const form = new FormData();
    form.append("file", file);
    const preview = await request<ModerationImportPreviewWire>("/api/v1/moderation/import/preview", {
      method: "POST",
      body: form,
    });
    return {
      rulesetName: preview.name,
      ruleCount: preview.ruleCount,
      referenceImageCount: preview.referenceImageCount,
    };
  },

  importRuleset: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<ModerationRuleset>("/api/v1/moderation/import", {
      method: "POST",
      body: form,
    });
  },
};
